import { f7, useStore } from 'framework7-react';
import { Router } from 'framework7/types';
import { FC, useCallback, useEffect, useState } from 'react';
import { PrepaidSettings } from './PrepaidSettings/PrepaidSettings';
import { AuthInfoState } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { getAndSetCustomerInfo } from '@/hooks/api/customer/useCustomerInfo';
import { usePaymentList } from '@/hooks/api/payment/usePaymentList';
import { usePrepaidSetting } from '@/hooks/api/prepaid/usePrepaidSetting';
import { useGmoRegisterAndEditRedirect } from '@/hooks/useGmoRegisterAndEditRedirect';
import { useLoadCustomer } from '@/hooks/useLoadCustomer';
import {
  BillInformation,
  Customer,
  PaymentMethod,
  Prepaid,
} from '@/types/api/customerApi';
import { storeDispatch } from '@/utils/store';

type PrepaidSettingsPageProps = {
  f7router: Router.Router;
  status?: string;
};

export const PrepaidSettingsPage: FC<PrepaidSettingsPageProps> = ({
  f7router,
  status,
}) => {
  const customerInfo = useStore(store, 'customerInfo') as Required<Customer>;
  const settingsPrevPage = store?.state.settingsPrevPage;
  const prevPage = settingsPrevPage || window.localStorage.getItem('prevPage');
  const prepaidInfo = useStore(
    store,
    'getFirstAdvancePaymentsInfo',
  ) as Required<Prepaid>;
  const billsInfo = useStore(
    store,
    'getBillInfo',
  ) as Required<BillInformation>[];
  const paymentMethods = useStore(
    store,
    'getPaymentMethodsInfo',
  ) as Required<PaymentMethod>[];
  const [isUpdating, setIsUpdating] = useState(false);
  const { isFetched, isFetching } = usePaymentList(false);
  const [currentStatus, setCurrentStatus] = useState(status);
  const authInfo = store?.state?.authInfo ?? ({} as AuthInfoState);
  const getStormCustomerId = authInfo?.m_customer_id ?? 0;
  const { bill, payment, isLoading } = usePrepaidSetting(
    customerInfo,
    billsInfo,
    prepaidInfo,
    paymentMethods,
  );
  const customerId = window.localStorage.getItem('m_customer_id');

  const token =
    window.localStorage.getItem('token') === null
      ? undefined
      : window.localStorage.getItem('token');

  const { isLoggedIn, resetIsLoggedIn } = useLoadCustomer(
    f7.views.main.router,
    token === null ? undefined : token,
    Number(customerId),
    currentStatus,
  );
  const { handleGmoRegisterAndEditRedirect } = useGmoRegisterAndEditRedirect();

  useEffect(() => {
    if (isLoggedIn) {
      handleGmoRegisterAndEditRedirect(currentStatus ?? '', isLoggedIn);
    }
  }, [isLoggedIn, currentStatus]);

  // ページを再レンダリングさせたい際に、別画面で呼び出す用の関数
  const handleIsUpdating = () => {
    setIsUpdating(true);
  };

  const afterGmoRedirect = useCallback(() => {
    if (currentStatus !== undefined && currentStatus !== '') {
      storeDispatch('setMenu', 'menu', prevPage);
    }

    switch (currentStatus) {
      // GMOペイメント post → maikuru バックエンド（api.mobicon.net）からのリダイレクト
      case 'gmo_success':
        if (isLoggedIn && !isFetched && !isFetching) {
          setCurrentStatus(undefined);
          resetIsLoggedIn();
          // データ取得はuseGmoRegisterAndEditRedirect（useEffect line 71-75）で処理済み
          // router.navigate()を呼ぶと同じURLに再ナビゲート → 無限ループになるため削除
        }
        break;
      case 'gmo_cancel':
        if (!isLoggedIn) return;
        setCurrentStatus(undefined);
        resetIsLoggedIn();
        getAndSetCustomerInfo({
          m_customer_id: getStormCustomerId,
        });
        // router.navigate()を呼ぶと同じURLに再ナビゲート → 無限ループになるため削除
        break;
      default:
        break;
    }
  }, [currentStatus, isFetched, isFetching, isLoggedIn, resetIsLoggedIn]);

  // GMOからのリダイレクト処理
  useEffect(() => {
    afterGmoRedirect();
  }, [afterGmoRedirect]);

  return (
    <PrepaidSettings
      f7router={f7router}
      status={status}
      isLoading={isLoading}
      bill={bill}
      prepaid={prepaidInfo}
      payment={payment}
      handleIsUpdating={handleIsUpdating}
      prevPage={prevPage ?? undefined}
    />
  );
};
